import React from "react";
import { useState, useMemo, useEffect } from "react";
import { Form } from "@dreambigger/design-system/src/components";
import { StepProps } from "../pages/flows/[flowId]";
import StepWrapper from "./step-wrapper";
import { useApplication, useCrossSell } from "../api";
import RadioCardFormItem from "../components/RadioCardFormItem";
import ProgressiveLoading from "../components/ProgressiveLoading";
import {
  SettingOutlined,
  SearchOutlined,
  StarOutlined,
  UserOutlined,
  CheckCircleOutlined,
  AppstoreOutlined,
} from "@ant-design/icons";
import { ResponseFields } from "@dreambigger/shared/src/types";

export interface CrossSellProduct {
  productCode?: string;
  description: string;
  title: string;
  optionsLabel: string;
  options: {
    label: string;
    value: string | boolean;
    description?: string;
    type?: "yes" | "no";
    activeColor?: string;
  }[];
  callout?: string;
  modalLaunchText?: string;
  modalTitle: string;
  modalContent: string;
}

export interface CrossSellResponse {
  title?: string;
  description?: string;
  products: CrossSellProduct[];
}

const BORDER_COLOR = "#bfbfbf";

const crossSellLoadingMessages = [
  { text: "Processing application data", icon: SettingOutlined },
  { text: "Analyzing your unique profile", icon: UserOutlined },
  {
    text: "Searching for tailored products just for you",
    icon: SearchOutlined,
  },
  { text: "Curating personalized offers", icon: StarOutlined },
  { text: "Refining best matches", icon: CheckCircleOutlined },
  {
    text: "Finalizing your exclusive product list and offers",
    icon: AppstoreOutlined,
  },
];

const CrossSell = ({ flow, step, brand, progress }: StepProps) => {
  const [disabled, setDisabled] = useState(true);
  const [form] = Form.useForm();
  const applicationHelper = useApplication(flow.financialInstitution.id);
  const application = useMemo(
    () => applicationHelper.find(flow.id, "draft"),
    [applicationHelper, flow]
  );
  const crossSellData = useCrossSell(
    application?.id,
    flow.financialInstitution.id
  );
  const response = useMemo(
    () => applicationHelper.findResponse(flow.id, "draft", step.slug),
    [application, flow, step]
  );

  useEffect(() => {
    setDisabled(true);
    form.resetFields();
  }, [step, response]);

  useEffect(() => {
    // If cross-sell data is still validating and no products are already loaded, disable the next button.
    if (crossSellData.isValidating && !crossSellData.data?.products) {
      setDisabled(true);
    }
    // If cross-sell data is not validating and there are no products or an error occurred, enable the next button.
    if (!crossSellData.isValidating) {
      if (!crossSellData.data?.products?.length || crossSellData.error) {
        setDisabled(false);
      }
    }
  }, [crossSellData.isValidating, crossSellData.data, crossSellData.error]);

  const checkDisabled = (values: ResponseFields) => {
    const hasUndefined = Object.values(values).some(
      (value) => value === undefined
    );
    setDisabled(hasUndefined);
  };

  // Override step title and description if provided in API response, allowing for dynamic promotions.
  const stepWithOverrides = {
    ...step,
    assets: {
      ...step.assets,
      // Do not show step title and description until the API response is received.
      ...(crossSellData.isValidating && { title: "" }),
      ...(crossSellData.isValidating && { description: "" }),
      // Use API response title and description if available.
      ...(crossSellData.data?.title && { title: crossSellData.data.title }),
      ...(crossSellData.data?.description && {
        description: crossSellData.data.description,
      }),
    },
  };

  const wrapperProps = {
    step: stepWithOverrides,
    brand,
    flow,
    progress,
    form,
    disabled,
  };

  if (!application) {
    return null;
  }

  const products = crossSellData.data?.products;

  if (!products && crossSellData.isValidating) {
    return (
      <StepWrapper {...wrapperProps}>
        <ProgressiveLoading
          loadingMessages={crossSellLoadingMessages}
          messageInterval={7000}
          progressDuration={20000}
        />
      </StepWrapper>
    );
  }

  if (crossSellData.error || !crossSellData.data) {
    return (
      <StepWrapper {...wrapperProps}>
        <p>
          There was an error loading product offers. Please contact support.
        </p>
      </StepWrapper>
    );
  }

  if (!products || !products.length) {
    return (
      <StepWrapper {...wrapperProps}>
        <p>No product offers are currently available. Please continue.</p>
      </StepWrapper>
    );
  }

  // Create a map to track title occurrences
  const titleOccurrences = new Map();

  return (
    <StepWrapper {...wrapperProps}>
      {products.map((product, _index) => {
        const {
          title,
          description,
          optionsLabel,
          options,
          callout,
          modalLaunchText = "Read More",
          modalTitle,
          modalContent,
        } = product;

        // Determine fieldName - use productCode if available, otherwise use title with suffix for duplicates
        let fieldName;

        if (product.productCode) {
          // Use productCode directly if available
          fieldName = product.productCode;
        } else {
          // Handle duplicate titles with incrementing suffixes
          const sanitizedTitle = title.replace(/\s+/g, "_");
          const count = titleOccurrences.get(sanitizedTitle) || 0;
          titleOccurrences.set(sanitizedTitle, count + 1);

          // First occurrence uses just the title, subsequent ones use __2, __3, etc.
          fieldName =
            count === 0 ? sanitizedTitle : `${sanitizedTitle}__${count + 1}`;
        }

        return (
          <RadioCardFormItem
            key={fieldName}
            className="mb-2"
            title={title}
            titleColor={brand.stepTitleColor}
            callout={callout}
            modalLaunchText={modalLaunchText}
            description={description}
            modalTitle={
              <div
                className="bb pb-2 mb-5"
                style={{ borderColor: BORDER_COLOR }}
                dangerouslySetInnerHTML={{ __html: modalTitle }}
              />
            }
            modalContent={
              <div dangerouslySetInnerHTML={{ __html: modalContent }} />
            }
            optionsLabel={optionsLabel}
            options={options}
            form={form}
            fieldName={fieldName}
            required
            onChange={checkDisabled}
          />
        );
      })}
    </StepWrapper>
  );
};

export default CrossSell;
